import React, { useEffect, useRef, useState, useCallback } from "react";
import slides from "./imageUrl";
import "./style.css";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Gallery = () => {
  const record = slides.photoset.photo;
  const images = record.map(
    (slide) =>
      `https://live.staticflickr.com/${slide.server}/${slide.id}_${slide.secret}_b.jpg`
  );

  const pinRef = useRef(null);
  const viewportRef = useRef(null);
  const cardRefs = useRef([]);
  const innerRefs = useRef([]);

  // ── Lightbox state ──
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isLightboxOpen = lightboxIndex !== null;

  const openLightbox = useCallback((index) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    () =>
      setLightboxIndex((i) => (i === null ? i : (i - 1 + images.length) % images.length)),
    [images.length]
  );
  const showNext = useCallback(
    () => setLightboxIndex((i) => (i === null ? i : (i + 1) % images.length)),
    [images.length]
  );

  // Lock background scroll + keyboard nav while the lightbox is open
  useEffect(() => {
    if (!isLightboxOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [isLightboxOpen, closeLightbox, showPrev, showNext]);

  useEffect(() => {
    const count = images.length;
    if (!count || !pinRef.current) return;

    const cards = cardRefs.current.filter(Boolean);
    const inners = innerRefs.current.filter(Boolean);
    const angleStep = 360 / count;

    // Responsive geometry — recalculated on layout() so the spiral keeps
    // its proportions across breakpoints instead of just clamping card size.
    let radius = 360;
    let vertGap = 104;
    let totalHeight = vertGap * count;

    // Only cards within this many degrees of dead-center stay visible —
    // kept wide on purpose so 1-2 neighboring cards are visible curving
    // away on either side of the front card (the spiral look). Overlap is
    // instead prevented on the vertical axis: vertGap (below) is sized to
    // always exceed the tallest a card can render at, so no two cards —
    // however close their angles — ever share the same vertical space.
    let visHalfAngle = 62;
    let cosThresh = Math.cos((visHalfAngle * Math.PI) / 180);

    const layout = () => {
      const w = pinRef.current.offsetWidth || window.innerWidth;
      let perspective;
      if (w <= 480) {
        radius = 150;
        vertGap = 150; // > tallest card at this breakpoint (~102px)
        visHalfAngle = 58;
        perspective = 780;
      } else if (w <= 768) {
        radius = 215;
        vertGap = 165; // > tallest card at this breakpoint (~102px)
        visHalfAngle = 60;
        perspective = 1000;
      } else if (w <= 1100) {
        radius = 300;
        vertGap = 195; // > tallest card at this breakpoint (~129px)
        visHalfAngle = 62;
        perspective = 1200;
      } else {
        radius = 380;
        vertGap = 260; // > tallest card at this breakpoint (~192px)
        visHalfAngle = 64;
        perspective = 1450;
      }
      totalHeight = vertGap * count;
      cosThresh = Math.cos((visHalfAngle * Math.PI) / 180);
      if (viewportRef.current) {
        viewportRef.current.style.perspective = `${perspective}px`;
      }
    };
    layout();

    // state.progress is driven purely by ScrollTrigger's scrub — there is
    // no ticking/autoplay loop, render() only runs on scroll updates.
    const state = { progress: 0 };

    const render = () => {
      // A single, uninterrupted 360° pass over the whole pin: every image
      // sweeps through dead-center exactly once, in order, with no resets
      // or seams — rotation and the vertical "climb" share the same phase
      // (both scaled by count) so a card is always vertically centered at
      // the exact moment it's angularly front-on.
      const rotation = state.progress * 360;
      const climb = state.progress * totalHeight;

      cards.forEach((card, i) => {
        if (!card) return;
        const angle = i * angleStep + rotation;

        // Facing angle relative to camera (0 = dead-center front, 180 = back)
        const norm = ((angle % 360) + 360) % 360;
        const facing = norm > 180 ? 360 - norm : norm;
        const facingRad = (facing * Math.PI) / 180;
        const rawCos = Math.cos(facingRad);
        const proximity = (rawCos + 1) / 2; // 0..1, used for scale/brightness

        // Sharp-edged visibility window with eased falloff (smoothstep) —
        // cards outside the window are fully transparent (and not
        // pointer-interactive) rather than faintly stacking up. Cards
        // inside the window (front card + its curving neighbors) stay
        // non-overlapping because of the vertGap sizing above, not
        // because of this window being narrow.
        let vis = (rawCos - cosThresh) / (1 - cosThresh);
        vis = Math.max(0, Math.min(1, vis));
        vis = vis * vis * (3 - 2 * vis);

        // Helical vertical position, phase-locked to rotation (see note
        // above) and wrapped so the stack recycles smoothly.
        let y = i * vertGap + climb + totalHeight / 2;
        y = (((y % totalHeight) + totalHeight) % totalHeight) - totalHeight / 2;

        card.style.transform = `translate3d(-50%, calc(-50% + ${y.toFixed(
          2
        )}px), 0) rotateY(${angle.toFixed(2)}deg) translateZ(${radius}px)`;
        card.style.zIndex = Math.round(proximity * 1000);
        card.style.pointerEvents = vis > 0.05 ? "auto" : "none";

        const inner = inners[i];
        if (inner) {
          const scale = 0.68 + proximity * 0.5;
          inner.style.transform = `scale(${scale.toFixed(3)})`;
          inner.style.opacity = vis.toFixed(3);
          inner.style.filter = `brightness(${(0.5 + proximity * 0.7).toFixed(
            3
          )}) blur(${((1 - vis) * 2.6).toFixed(2)}px)`;
          inner.classList.toggle("is-active", proximity > 0.985);
        }
      });
    };

    render();

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: pinRef.current,
        start: "top top",
        end: () => "+=" + Math.round(window.innerHeight * 2.8),
        pin: true,
        scrub: 1.4,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          state.progress = self.progress;
          render();
        },
        onRefreshInit: layout,
        onRefresh: render,
      });
    }, pinRef);

    const handleResize = () => {
      layout();
      render();
      ScrollTrigger.refresh();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      ctx.revert();
    };
  }, [images.length]);

  return (
    <section className="gallery-section" id="gallery">
      {/* 3D spiral gallery — scroll-scrubbed, no autoplay.
          The header now lives INSIDE the pinned element (not above it),
          so it is pinned in place together with the spiral for the whole
          scroll duration instead of scrolling away before the spiral
          finishes.
          Note: the fade-in wrapper is a separate element from the element
          GSAP pins/writes inline transforms to, so framer-motion's own
          style updates on the wrapper never clobber GSAP's per-frame
          inline styles on the pinned node (or vice versa). */}
      <motion.div
        className="gallery-spiral-fadein"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="gallery-spiral-pin" ref={pinRef}>
          <div className="udg-section-header gallery-header">
            <motion.span
              className="udg-eyebrow"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Highlights
            </motion.span>
            <motion.h2
              className="udg-section-title font-display"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              Gallery
            </motion.h2>
            <motion.span
              className="udg-gold-rule"
              style={{ margin: "0 auto", transformOrigin: "left" }}
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
            />
          </div>
          <div className="gallery-spiral-viewport" ref={viewportRef}>
            <div className="gallery-spiral-stage">
              {images.map((src, index) => (
                <div
                  className="spiral-card"
                  key={index}
                  ref={(el) => (cardRefs.current[index] = el)}
                >
                  <div
                    className="spiral-card-inner"
                    ref={(el) => (innerRefs.current[index] = el)}
                    onClick={() => openLightbox(index)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open image ${index + 1} of ${images.length}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openLightbox(index);
                      }
                    }}
                  >
                    <img
                      src={src}
                      className="spiral-img"
                      alt={`Udghosh event ${index + 1}`}
                      loading="lazy"
                      draggable="false"
                    />
                    <span className="spiral-card-sheen" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="gallery-spiral-fade gallery-spiral-fade-top" />
          <div className="gallery-spiral-fade gallery-spiral-fade-bottom" />
        </div>
      </motion.div>

      {/* ── Lightbox: click any card to enlarge it ── */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            className="gallery-lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeLightbox}
          >
            <button
              className="gallery-lightbox-close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              &times;
            </button>

            <button
              className="gallery-lightbox-nav gallery-lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
              aria-label="Previous image"
            >
              &#8249;
            </button>

            <motion.img
              key={lightboxIndex}
              src={images[lightboxIndex]}
              alt={`Udghosh event ${lightboxIndex + 1}`}
              className="gallery-lightbox-image"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            />

            <button
              className="gallery-lightbox-nav gallery-lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              aria-label="Next image"
            >
              &#8250;
            </button>

            <div className="gallery-lightbox-counter">
              {lightboxIndex + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;